/*
 * Copyright (c) 2024. Tooling Team
 */

import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import Popover from "react-bootstrap/Popover";
import { Button } from "@uhg-abyss/web/ui/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { AllTagsForm } from "./AllTagsForm";
import SlimPlusIcon from "../assets/icons/PlusIconSlim28.svg";

export function MapTags(props) {
  const { allTags, mapTags, applyTags, isCommonMap } = props;
  const [tags, setTags] = useState({});
  const [currTags, setCurrTags] = useState({});
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const initialRender = useRef(true);

  useEffect(() => {
    const tags = tagsSchema();
    const tagsClone = _.cloneDeep(tags);
    setTags(tags);
    setCurrTags(tagsClone);
  }, []);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (!_.isEqual(currTags, tags)) {
      setIsApplyDisabled(false);
    } else {
      setIsApplyDisabled(true);
    }
  }, [tags]);

  const tagsSchema = () => {
    const tempTags = {};
    const appliedTags = appliedTagsSchema();

    allTags.map((tag) => {
      tempTags[tag.name] = tag.values.map((val, index) => {
        const isSelected =
          Object.keys(appliedTags).length && appliedTags[tag.name]
            ? appliedTags[tag.name].includes(val)
            : false;

        return {
          id: index,
          name: val,
          selected: isSelected,
        };
      });
    });
    return tempTags;
  };

  const appliedTagsSchema = () => {
    const tempTags = {};
    mapTags.map((tag) => {
      if (!tempTags[tag.name] || !tempTags[tag.name].length) {
        tempTags[tag.name] = [];
      }
      tempTags[tag.name].push(tag.value);
    });
    return tempTags;
  };

  const handleApplyTags = () => {
    const tempTags = { ...tags };
    const newTags = [];

    // Get selected Tags
    Object.keys(tempTags).forEach((tagType) => {
      const tempSelected = {
        name: tagType,
        values: [],
      };
      tempTags[tagType].map((tag) => {
        if (tag.selected) {
          tempSelected.values.push(tag.name);
        }
      });
      if (tempSelected.values.length) {
        newTags.push(tempSelected);
      }
    });

    document.body.click(); // close the popover

    // Transform selected tags for request
    applyTags(newTags);
  };

  const removeTag = (e, tag, tagType) => {
    const tempTags = { ...tags };
    tempTags[tagType][tag.id].selected = false;
    setTags(tempTags);
  };

  const popover = (props, tagType) => (
    <Popover {...props}>
      <Popover.Header as="h3">Manage Tags</Popover.Header>
      <Popover.Body className="text-break">
        <AllTagsForm
          tags={tags}
          tagType={tagType}
          setTags={setTags}
        ></AllTagsForm>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <h3>Map Tags</h3>
      <div className="row">
        <div className="col mt-2">
          <div className="d-flex flex-grow-1 justify-content-evenly p-2 selected-attributes-box">
            {Object.keys(tags).map((tagType, index) => (
              <div key={index} className="d-flex justify-content-center mt-2">
                <div
                  key={index}
                  className="d-flex align-items-center flex-column"
                >
                  <div className="attr-name">{tagType}</div>
                  <div className="d-flex flex-row flex-wrap align-items-center">
                    {tags[tagType].map((tag, i) => {
                      if (tag.selected) {
                        return (
                          <div key={i} className="map-tag bg-primary p-2 m-1">
                            <span
                              title={`${tag.name} Tag`}
                              className="tag-name d-inline p-1"
                            >
                              {tag.name}
                            </span>
                            {!isCommonMap ? (
                              <span
                                title={`Remove Tag ${tag.name}`}
                                className="remove-tag d-inline p-1"
                                onClick={(e) => removeTag(e, tag, tagType)}
                              >
                                <FontAwesomeIcon icon={solid("xmark")} />
                              </span>
                            ) : (
                              <></>
                            )}
                          </div>
                        );
                      }
                    })}

                    <OverlayTrigger
                      rootClose
                      trigger="click"
                      placement="top"
                      shouldUpdatePosition={true}
                      overlay={(props) => popover(props, tagType)}
                    >
                      {!isCommonMap ? (
                        <div className="d-flex align-items-center map-tag-add-icon p-2">
                          <img
                            title={`Add ${tagType} Tag`}
                            src={SlimPlusIcon}
                            alt="Add Tag"
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            ))}

            <div className="d-flex align-self-center p-2 badge">
              <Button
                variant="outline"
                isDisabled={isApplyDisabled || isCommonMap}
                onClick={() => handleApplyTags()}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

MapTags.propTypes = {
  allTags: PropTypes.array,
  mapTags: PropTypes.array,
  applyTags: PropTypes.func,
  isCommonMap: PropTypes.bool,
};
